import { z } from 'zod';

// Backend expected condition types
const FormulaConditionType = z.enum(['threshold', 'trend']);

// Backend expected amount types
export const FormulaAmountType = z.enum([
  'requested_amount',
  'finalized_amount',
  'finalized_amount_for_initial_budget',
  'finalized_amount_for_supplementary_budget',
  'finalized_amount_for_carried_forward',
  'finalized_amount_for_reserve',
  'execution_amount',
  'execution_rate',
]);

// Backend expected trend conditions
export const FormulaTrendCondition = z.enum([
  'increase_gte',
  'increase_gte_percent',
  'decrease_gte',
  'decrease_gte_percent',
  'no_change',
]);

// Single formula condition structure matching backend expectations
const FormulaConditionSchema = z.object({
  condition_type: FormulaConditionType,
  base_amount_year: z.number(),
  base_amount_type: FormulaAmountType,
  base_amount_value_from: z.number().nullable(),
  base_amount_value_to: z.number().nullable(),
  base_amount_value_isnull: z.boolean(),
  target_amount_year: z.number().nullable(),
  target_amount_type: FormulaAmountType.nullable(),
  trend_condition: FormulaTrendCondition.nullable(),
  trend_condition_value: z.number().nullable(),
});

export type FormulaCondition = z.infer<typeof FormulaConditionSchema>;

// Map frontend amount types to backend format
export const amountTypeToBackendMap: Record<string, string> = {
  requested: 'requested_amount',
  finalized: 'finalized_amount',
  executed: 'executed_amount',
  execution_rate: 'execution_rate',
};

// Map frontend budget types to backend amount types
export const budgetTypeToBackendAmountTypeMap: Record<
  string,
  Record<string, string>
> = {
  initial: {
    finalized: 'finalized_amount_for_initial_budget',
  },
  supplementary: {
    finalized: 'finalized_amount_for_supplementary_budget',
  },
  'carried-forward': {
    finalized: 'finalized_amount_for_carried_forward',
  },
  reserve: {
    finalized: 'finalized_amount_for_reserve',
  },
  all: {
    requested: 'requested_amount',
    finalized: 'finalized_amount',
    executed: 'executed_amount',
    execution_rate: 'execution_rate',
  },
};

// Map frontend operators to backend trend conditions
export const operatorToTrendConditionMap: Record<
  string,
  Record<string, string>
> = {
  gte: {
    amount: 'increase_gte',
    percent: 'increase_gte_percent',
  },
  lte: {
    amount: 'decrease_gte',
    percent: 'decrease_gte_percent',
  },
  eq: {
    amount: 'no_change',
    percent: 'no_change',
  },
};
